/**
 * @biomax/plugin-supply-chain — supply-side constraint modelling.
 *
 * v0.1 scope (scaffold-grade):
 *   - Supply class catalog (PPE / oxygen / ventilator / cold chain /
 *     lab reagent / therapeutic / vaccine / test kit)
 *   - Per-class supply parameters: per-capita daily burn, current
 *     stockpile, replenishment lead time, manufacturer concentration
 *   - projectStockout — given a demand projection + supply parameters,
 *     compute days until exhaustion + replenishment-failure risk
 *
 * v0.4 adds: per-region inventory, allocation-fairness optimiser,
 * counterfeit-vaccine risk model.
 */

import type { PluginManifest } from '@biomax/types';

export type SupplyClass =
  | 'ppe-n95'
  | 'ppe-surgical-mask'
  | 'ppe-gown'
  | 'ppe-gloves'
  | 'oxygen-cylinder'
  | 'oxygen-concentrator'
  | 'ventilator-invasive'
  | 'ventilator-noninvasive'
  | 'cold-chain-ultra-low-freezer'
  | 'cold-chain-standard-freezer'
  | 'lab-pcr-reagent'
  | 'lab-vtm-swab'
  | 'lab-sequencing-reagent'
  | 'therapeutic-antiviral'
  | 'therapeutic-mab'
  | 'vaccine-mrna'
  | 'vaccine-viral-vector'
  | 'test-kit-rapid'
  | 'test-kit-pcr';

export interface SupplyClassSpec {
  id: SupplyClass;
  label: string;
  /** Typical daily per-capita burn during active outbreak (per 100k population). */
  baselineBurnPer100k: number;
  /** Surge multiplier during severe outbreak (e.g., 5× baseline). */
  surgeBurnMultiplier: number;
  /** Average production lead time, days (raw material → distribution). */
  productionLeadTimeDays: number;
  /** Shelf life, days. */
  shelfLifeDays: number;
  /** Manufacturer-concentration index: 0 = perfectly distributed, 1 = single-source. */
  manufacturerConcentration: number;
  /** Citations supporting the parameters. */
  citations: { source: string; note?: string }[];
}

export const SUPPLY_CATALOG: Record<SupplyClass, SupplyClassSpec> = {
  'ppe-n95': {
    id: 'ppe-n95', label: 'N95 respirator',
    baselineBurnPer100k: 200, surgeBurnMultiplier: 20,
    productionLeadTimeDays: 90, shelfLifeDays: 1825,
    manufacturerConcentration: 0.7,
    citations: [{ source: '3M + Honeywell 2020 production capacity reports', note: 'Pre-pandemic ~85% of US N95 manufactured by 3M.' }],
  },
  'ppe-surgical-mask': {
    id: 'ppe-surgical-mask', label: 'Surgical mask',
    baselineBurnPer100k: 800, surgeBurnMultiplier: 10,
    productionLeadTimeDays: 45, shelfLifeDays: 1825,
    manufacturerConcentration: 0.65,
    citations: [{ source: 'COVID-19 PPE shortage analyses 2020', note: 'China responsible for ~50% global mask production pre-pandemic.' }],
  },
  'ppe-gown': {
    id: 'ppe-gown', label: 'Isolation gown',
    baselineBurnPer100k: 150, surgeBurnMultiplier: 8,
    productionLeadTimeDays: 60, shelfLifeDays: 1825,
    manufacturerConcentration: 0.55,
    citations: [{ source: 'WHO PPE supply guidance 2020' }],
  },
  'ppe-gloves': {
    id: 'ppe-gloves', label: 'Examination gloves',
    baselineBurnPer100k: 5000, surgeBurnMultiplier: 5,
    productionLeadTimeDays: 30, shelfLifeDays: 1095,
    manufacturerConcentration: 0.50,
    citations: [{ source: 'Malaysia + Thailand glove industry — ~70% global production' }],
  },
  'oxygen-cylinder': {
    id: 'oxygen-cylinder', label: 'Medical oxygen (cylinder)',
    baselineBurnPer100k: 50, surgeBurnMultiplier: 15,
    productionLeadTimeDays: 7, shelfLifeDays: 0,
    manufacturerConcentration: 0.40,
    citations: [{ source: 'WHO + UNICEF oxygen-supply guidance 2021', note: 'India May 2021 oxygen crisis is reference case.' }],
  },
  'oxygen-concentrator': {
    id: 'oxygen-concentrator', label: 'Oxygen concentrator',
    baselineBurnPer100k: 5, surgeBurnMultiplier: 20,
    productionLeadTimeDays: 60, shelfLifeDays: 0,
    manufacturerConcentration: 0.65,
    citations: [{ source: 'Philips + Invacare concentrator production data 2021' }],
  },
  'ventilator-invasive': {
    id: 'ventilator-invasive', label: 'Invasive ventilator',
    baselineBurnPer100k: 2, surgeBurnMultiplier: 8,
    productionLeadTimeDays: 90, shelfLifeDays: 0,
    manufacturerConcentration: 0.55,
    citations: [{ source: 'Hamilton + Draeger + GE + Medtronic 2020 ventilator surge production' }],
  },
  'ventilator-noninvasive': {
    id: 'ventilator-noninvasive', label: 'NIV / HFNC ventilator',
    baselineBurnPer100k: 5, surgeBurnMultiplier: 10,
    productionLeadTimeDays: 60, shelfLifeDays: 0,
    manufacturerConcentration: 0.45,
    citations: [{ source: 'ResMed + Vapotherm 2020-21 production data' }],
  },
  'cold-chain-ultra-low-freezer': {
    id: 'cold-chain-ultra-low-freezer', label: '-70°C ultra-low freezer',
    baselineBurnPer100k: 0.5, surgeBurnMultiplier: 10,
    productionLeadTimeDays: 120, shelfLifeDays: 0,
    manufacturerConcentration: 0.70,
    citations: [{ source: 'Thermo Fisher + Eppendorf + Stirling 2020-21 freezer production reports', note: 'Critical for BNT162b2 mRNA vaccine.' }],
  },
  'cold-chain-standard-freezer': {
    id: 'cold-chain-standard-freezer', label: '-20°C standard freezer',
    baselineBurnPer100k: 2, surgeBurnMultiplier: 3,
    productionLeadTimeDays: 60, shelfLifeDays: 0,
    manufacturerConcentration: 0.50,
    citations: [{ source: 'WHO PQS cold-chain equipment list' }],
  },
  'lab-pcr-reagent': {
    id: 'lab-pcr-reagent', label: 'PCR master-mix reagent',
    baselineBurnPer100k: 200, surgeBurnMultiplier: 30,
    productionLeadTimeDays: 30, shelfLifeDays: 730,
    manufacturerConcentration: 0.65,
    citations: [{ source: 'Roche + Thermo + Qiagen 2020 PCR reagent supply analyses', note: 'Concentrated supply caused 2020 testing capacity ceiling.' }],
  },
  'lab-vtm-swab': {
    id: 'lab-vtm-swab', label: 'Viral transport media + swab',
    baselineBurnPer100k: 500, surgeBurnMultiplier: 25,
    productionLeadTimeDays: 14, shelfLifeDays: 365,
    manufacturerConcentration: 0.55,
    citations: [{ source: 'Copan Italy March 2020 supply shock', note: 'Single dominant manufacturer (Brescia) in major outbreak zone.' }],
  },
  'lab-sequencing-reagent': {
    id: 'lab-sequencing-reagent', label: 'NGS sequencing reagent',
    baselineBurnPer100k: 10, surgeBurnMultiplier: 8,
    productionLeadTimeDays: 60, shelfLifeDays: 365,
    manufacturerConcentration: 0.80,
    citations: [{ source: 'Illumina + Oxford Nanopore reagent supply 2021-22', note: 'Highly concentrated: Illumina dominant. Africa sequencing throughput limited by reagent access.' }],
  },
  'therapeutic-antiviral': {
    id: 'therapeutic-antiviral', label: 'Oral antiviral (paxlovid / molnupiravir-class)',
    baselineBurnPer100k: 50, surgeBurnMultiplier: 20,
    productionLeadTimeDays: 180, shelfLifeDays: 730,
    manufacturerConcentration: 0.65,
    citations: [{ source: 'Pfizer paxlovid + Merck molnupiravir 2022 production capacity' }],
  },
  'therapeutic-mab': {
    id: 'therapeutic-mab', label: 'Monoclonal antibody (IV)',
    baselineBurnPer100k: 5, surgeBurnMultiplier: 15,
    productionLeadTimeDays: 365, shelfLifeDays: 730,
    manufacturerConcentration: 0.75,
    citations: [{ source: 'Lilly + Regeneron + GSK/Vir mAb 2020-22 production capacity' }],
  },
  'vaccine-mrna': {
    id: 'vaccine-mrna', label: 'mRNA vaccine (Pfizer/Moderna)',
    baselineBurnPer100k: 0, surgeBurnMultiplier: 50_000,
    productionLeadTimeDays: 180, shelfLifeDays: 180,
    manufacturerConcentration: 0.55,
    citations: [{ source: 'Pfizer-BioNTech + Moderna 2021-22 production scale-up' }],
  },
  'vaccine-viral-vector': {
    id: 'vaccine-viral-vector', label: 'Viral-vector vaccine',
    baselineBurnPer100k: 0, surgeBurnMultiplier: 30_000,
    productionLeadTimeDays: 90, shelfLifeDays: 365,
    manufacturerConcentration: 0.60,
    citations: [{ source: 'AstraZeneca/Oxford + J&J 2021 production' }],
  },
  'test-kit-rapid': {
    id: 'test-kit-rapid', label: 'Antigen rapid test',
    baselineBurnPer100k: 100, surgeBurnMultiplier: 100,
    productionLeadTimeDays: 60, shelfLifeDays: 365,
    manufacturerConcentration: 0.45,
    citations: [{ source: 'Abbott BinaxNOW + iHealth + Roche SD Biosensor production' }],
  },
  'test-kit-pcr': {
    id: 'test-kit-pcr', label: 'PCR test kit (commercial)',
    baselineBurnPer100k: 50, surgeBurnMultiplier: 40,
    productionLeadTimeDays: 30, shelfLifeDays: 365,
    manufacturerConcentration: 0.55,
    citations: [{ source: 'Roche cobas + Cepheid GeneXpert production' }],
  },
};

export interface StockoutProjectionParams {
  /** Daily demand projection (e.g., from outbreak simulation). */
  dailyDemand: number[];
  /** Current stockpile (units). */
  currentStockpile: number;
  /** Steady-state daily replenishment (units / day). */
  replenishmentPerDay: number;
  /** Production lead time before any replenishment starts. */
  replenishmentLeadTimeDays?: number;
  /** Cap on replenishment (e.g., manufacturer surge limit). */
  replenishmentCapPerDay?: number;
}

export interface StockoutProjection {
  /** Day-by-day inventory. */
  inventory: number[];
  /** Day at which inventory first reaches 0, or null if never. */
  stockoutDay: number | null;
  /** Cumulative unmet demand (units short of need across the window). */
  cumulativeUnmet: number;
}

export function projectStockout(params: StockoutProjectionParams): StockoutProjection {
  const leadTime = params.replenishmentLeadTimeDays ?? 0;
  const cap = params.replenishmentCapPerDay ?? Infinity;
  let inventory = params.currentStockpile;
  const trajectory: number[] = [];
  let stockoutDay: number | null = null;
  let unmet = 0;
  for (let t = 0; t < params.dailyDemand.length; t++) {
    const replenish = t >= leadTime ? Math.min(params.replenishmentPerDay, cap) : 0;
    inventory += replenish;
    const demand = params.dailyDemand[t] ?? 0;
    if (inventory >= demand) {
      inventory -= demand;
    } else {
      unmet += demand - inventory;
      inventory = 0;
      if (stockoutDay === null) stockoutDay = t;
    }
    trajectory.push(inventory);
  }
  return { inventory: trajectory, stockoutDay, cumulativeUnmet: unmet };
}

/**
 * Citations for the supply-chain math + operational framework. PMIDs
 * round-tripped via @biomax/plugin-literature-fetch on verifiedAt.
 *
 * Note: SUPPLY_CATALOG entries (per-class burn rates, surge multipliers,
 * lead times, manufacturer concentration) are sourced predominantly
 * from industry reports + WHO/HHS gray literature — NOT peer-reviewed
 * journal articles. Those citation fields therefore carry source-strings
 * only, no PMIDs. The structured PMID-backed references below cover
 * the engine math (Meltzer demand modeling, Hick surge tiers) and the
 * scarce-resource-allocation framework (Emanuel 2020 NEJM).
 *
 * Re-run scripts/verify-references.ts to revalidate.
 */
export const SUPPLY_CHAIN_REFERENCES = [
  {
    id: 'meltzer-2015-ventilation',
    label: 'Meltzer et al. 2015 Clin Infect Dis — "Estimates of the Demand for Mechanical Ventilation in the United States During an Influenza Pandemic"',
    pmid: '25878301',
    doi: '10.1093/cid/civ089',
    appliesTo: 'projectStockout (demand-modeling pattern)',
    note: 'Foundational US demand-modeling reference for ventilator surge planning. The dailyDemand parameter to projectStockout follows the cohort-by-day pattern this paper formalises.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'meltzer-2015-antivirals',
    label: 'Meltzer et al. 2015 Clin Infect Dis — "Estimating the United States Demand for Influenza Antivirals and the Effect on Severe Influenza Disease"',
    pmid: '25878299',
    doi: '10.1093/cid/civ084',
    appliesTo: 'SUPPLY_CATALOG (antiviral burn-rate framework)',
    note: 'Companion to the ventilator demand paper; backs the per-class burn-rate framework for therapeutic stockpiles.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'hick-2009-surge',
    label: 'Hick et al. 2009 Disaster Med Public Health Prep — "Refining Surge Capacity: Conventional, Contingency, and Crisis Capacity"',
    pmid: '19349869',
    doi: '10.1097/dmp.0b013e31819f1ae2',
    appliesTo: 'projectStockout (surge-tier framing)',
    note: 'Established the conventional / contingency / crisis surge-tier framework. surgeBurnMultiplier values in SUPPLY_CATALOG correspond to these tiers.',
    verifiedAt: '2026-05-28',
  },
  {
    id: 'emanuel-2020',
    label: 'Emanuel et al. 2020 NEJM — "Fair Allocation of Scarce Medical Resources in the Time of Covid-19"',
    pmid: '32202722',
    doi: '10.1056/nejmsb2005114',
    appliesTo: 'cumulativeUnmet (allocation ethics framework)',
    note: 'When projectStockout reports cumulativeUnmet > 0, the consumer (§7.8 surface) must implement an allocation framework. Emanuel 2020 NEJM is the canonical peer-reviewed framework for that triage decision.',
    verifiedAt: '2026-05-28',
  },
] as const;

export const manifest: PluginManifest = {
  id: 'supply-chain', version: '0.1.0',
  engineCompat: '^0.5.0', kingdoms: '*', systems: [], components: [],
};
